import React from 'react';
import { Link } from 'react-router-dom';
import { FaCheck, FaTimes, FaStar, FaLightbulb, FaBookOpen, FaHeadphones, FaUsers, FaGraduationCap, FaClock, FaHeart } from 'react-icons/fa';
import SEO from '../components/SEO';

const IsPersianHardToLearn: React.FC = () => {
  const difficultyFactors = [
    {
      factor: "Alphabet & Script",
      difficulty: "Medium",
      description: "Persian uses a modified Arabic script with 32 letters",
      challenges: [
        "Different writing direction (right-to-left)",
        "Letters change shape based on position",
        "Some letters look similar but sound different"
      ],
      advantages: [
        "Consistent pronunciation rules",
        "No silent letters",
        "Phonetic spelling system"
      ]
    },
    {
      factor: "Grammar Structure",
      difficulty: "Medium",
      description: "Persian grammar is relatively straightforward compared to many languages",
      challenges: [
        "Verb conjugations can be complex",
        "Word order is flexible but has rules",
        "Some grammatical concepts are different from English"
      ],
      advantages: [
        "No grammatical gender",
        "No articles (a, an, the)",
        "Regular verb patterns"
      ]
    },
    {
      factor: "Pronunciation",
      difficulty: "Easy to Medium",
      description: "Persian pronunciation is generally phonetic and consistent",
      challenges: [
        "Some sounds don't exist in English",
        "Stress patterns are different",
        "Intonation affects meaning"
      ],
      advantages: [
        "Consistent sound-letter correspondence",
        "No silent letters",
        "Clear pronunciation rules"
      ]
    },
    {
      factor: "Vocabulary",
      difficulty: "Medium",
      description: "Persian vocabulary has both familiar and unfamiliar elements",
      challenges: [
        "Limited cognates with English",
        "Many words from Arabic origin",
        "Cultural context needed for some words"
      ],
      advantages: [
        "Logical word formation",
        "Many compound words",
        "Rich descriptive vocabulary"
      ]
    }
  ];

  const learningTips = [
    {
      icon: <FaBookOpen className="w-8 h-8 text-blue-600" />,
      title: "Start with the Alphabet",
      description: "Master the Persian script early - it's the foundation for everything else",
      tips: [
        "Practice writing each letter in all positions",
        "Learn the basic sounds and combinations",
        "Read simple words and phrases daily",
        "Use apps with audio pronunciation"
      ]
    },
    {
      icon: <FaHeadphones className="w-8 h-8 text-green-600" />,
      title: "Focus on Pronunciation",
      description: "Get pronunciation right from the start to avoid bad habits",
      tips: [
        "Listen to native speakers regularly",
        "Practice with audio recordings",
        "Record yourself and compare",
        "Learn the stress patterns"
      ]
    },
    {
      icon: <FaUsers className="w-8 h-8 text-purple-600" />,
      title: "Practice with Native Speakers",
      description: "Real conversation practice accelerates learning significantly",
      tips: [
        "Join Persian language communities",
        "Find language exchange partners",
        "Practice with teachers regularly",
        "Don't be afraid to make mistakes"
      ]
    },
    {
      icon: <FaGraduationCap className="w-8 h-8 text-orange-600" />,
      title: "Learn Grammar in Context",
      description: "Understand grammar through real examples, not just rules",
      tips: [
        "Study grammar through dialogues",
        "Practice with interactive exercises",
        "Learn cultural context behind expressions",
        "Focus on common patterns first"
      ]
    }
  ];

  const comparisonWithOtherLanguages = [
    {
      language: "Arabic",
      difficulty: "Harder",
      reasons: [
        "More complex grammar",
        "More difficult pronunciation",
        "More complex writing system",
        "More regional variations"
      ]
    },
    {
      language: "Chinese",
      difficulty: "Much Easier",
      reasons: [
        "No tones to master",
        "Alphabetic writing system",
        "Simpler grammar structure",
        "More consistent pronunciation"
      ]
    },
    {
      language: "Russian",
      difficulty: "Similar",
      reasons: [
        "Similar grammar complexity",
        "Different alphabet but easier",
        "Similar pronunciation difficulty",
        "Both have rich literary traditions"
      ]
    },
    {
      language: "Spanish",
      difficulty: "Harder",
      reasons: [
        "Different writing system",
        "Less familiar vocabulary",
        "Different cultural context",
        "More complex verb system"
      ]
    }
  ];

  const successStories = [
    {
      name: "Emily Chen",
      background: "English speaker, no previous language experience",
      timeline: "6 months to basic conversation",
      achievement: "Can hold basic conversations and read simple texts",
      quote: "Persian was easier than I expected. The consistent pronunciation and logical grammar made it much more approachable than I thought."
    },
    {
      name: "Marcus Rodriguez",
      background: "Spanish speaker, some Arabic knowledge",
      timeline: "3 months to intermediate level",
      achievement: "Can read Persian literature and discuss complex topics",
      quote: "Having some Arabic background helped with the script, but Persian grammar is much more straightforward than Arabic."
    },
    {
      name: "Sarah Kim",
      background: "Korean speaker, studied multiple languages",
      timeline: "1 year to advanced proficiency",
      achievement: "Fluent in Persian, can teach the language",
      quote: "Persian is beautiful and logical. The hardest part was the script, but once I got that, everything fell into place."
    }
  ];

  return (
    <>
      <SEO 
        title="Is Persian Hard to Learn? Complete Guide | پیروز Pirooz"
        description="Is Persian hard to learn? Get the complete answer with difficulty analysis, learning tips, and success stories. Discover why Persian might be easier than you think!"
        keywords={[
          // Primary Target Keywords
          'is persian hard to learn', 'how hard is persian to learn', 'persian language difficulty',
          'is farsi hard to learn', 'how hard is farsi to learn', 'farsi language difficulty',
          'persian learning difficulty', 'farsi learning difficulty', 'persian vs other languages',
          'farsi vs other languages', 'persian language complexity', 'farsi language complexity',
          
          // Secondary Variations
          'how difficult is persian', 'how difficult is farsi', 'persian language challenge',
          'farsi language challenge', 'persian learning challenge', 'farsi learning challenge',
          'persian language complexity', 'farsi language complexity', 'persian learning complexity',
          'farsi learning complexity', 'persian language learning difficulty', 'farsi language learning difficulty',
          
          // Time-Based Keywords
          'how long to learn persian', 'how long to learn farsi', 'persian learning time',
          'farsi learning time', 'persian language learning curve', 'farsi language learning curve',
          'persian learning timeline', 'farsi learning timeline', 'persian learning duration',
          'farsi learning duration', 'persian learning period', 'farsi learning period',
          
          // Component-Specific Keywords
          'persian grammar difficulty', 'farsi grammar difficulty', 'persian grammar complexity',
          'farsi grammar complexity', 'persian grammar challenge', 'farsi grammar challenge',
          'persian alphabet difficulty', 'farsi alphabet difficulty', 'persian script difficulty',
          'farsi script difficulty', 'persian writing difficulty', 'farsi writing difficulty',
          'persian pronunciation difficulty', 'farsi pronunciation difficulty', 'persian speaking difficulty',
          'farsi speaking difficulty', 'persian vocabulary difficulty', 'farsi vocabulary difficulty',
          
          // Comparison Keywords
          'persian vs arabic difficulty', 'farsi vs arabic difficulty', 'persian vs arabic learning',
          'farsi vs arabic learning', 'persian vs chinese difficulty', 'farsi vs chinese difficulty',
          'persian vs russian difficulty', 'farsi vs russian difficulty', 'persian vs spanish difficulty',
          'farsi vs spanish difficulty', 'persian vs english difficulty', 'farsi vs english difficulty',
          'persian vs french difficulty', 'farsi vs french difficulty', 'persian vs german difficulty',
          'farsi vs german difficulty', 'persian vs japanese difficulty', 'farsi vs japanese difficulty',
          
          // Skill Level Keywords
          'persian for beginners difficulty', 'farsi for beginners difficulty', 'persian beginner challenge',
          'farsi beginner challenge', 'persian intermediate difficulty', 'farsi intermediate difficulty',
          'persian advanced difficulty', 'farsi advanced difficulty', 'persian fluency difficulty',
          'farsi fluency difficulty', 'persian mastery difficulty', 'farsi mastery difficulty',
          
          // Learning Method Keywords
          'learn persian fast', 'learn farsi fast', 'persian fast learning', 'farsi fast learning',
          'persian quick learning', 'farsi quick learning', 'persian accelerated learning',
          'farsi accelerated learning', 'persian intensive learning', 'farsi intensive learning',
          'persian immersion learning', 'farsi immersion learning', 'persian self-study difficulty',
          'farsi self-study difficulty', 'persian online learning difficulty', 'farsi online learning difficulty',
          
          // Success Keywords
          'persian learning success', 'farsi learning success', 'persian learning achievement',
          'farsi learning achievement', 'persian learning progress', 'farsi learning progress',
          'persian learning results', 'farsi learning results', 'persian learning outcomes',
          'farsi learning outcomes', 'persian learning success stories', 'farsi learning success stories',
          
          // Motivation Keywords
          'persian learning motivation', 'farsi learning motivation', 'persian learning inspiration',
          'farsi learning inspiration', 'persian learning encouragement', 'farsi learning encouragement',
          'persian learning confidence', 'farsi learning confidence', 'persian learning mindset',
          'farsi learning mindset', 'persian learning attitude', 'farsi learning attitude',
          
          // Expert Opinion Keywords
          'persian language expert opinion', 'farsi language expert opinion', 'persian teacher advice',
          'farsi teacher advice', 'persian linguist opinion', 'farsi linguist opinion',
          'persian language research', 'farsi language research', 'persian learning studies',
          'farsi learning studies', 'persian language analysis', 'farsi language analysis',
          
          // Related Keywords
          'persian language facts', 'farsi language facts', 'persian language information',
          'farsi language information', 'persian language guide', 'farsi language guide',
          'persian language overview', 'farsi language overview', 'persian language introduction',
          'farsi language introduction', 'persian language basics', 'farsi language basics'
        ]}
        canonicalUrl="/is-persian-hard-to-learn"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Is Persian Hard to Learn? Complete Difficulty Analysis",
          "description": "Comprehensive guide to Persian language difficulty, including grammar, pronunciation, alphabet, and learning tips",
          "author": {
            "@type": "Organization",
            "name": "پیروز Pirooz"
          },
          "publisher": {
            "@type": "Organization",
            "name": "پیروز Pirooz",
            "logo": {
              "@type": "ImageObject",
              "url": "https://farsi-app.com/logo512.png"
            }
          },
          "datePublished": "2025-01-21",
          "dateModified": "2025-01-21",
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": "https://farsi-app.com/is-persian-hard-to-learn"
          },
          "image": "https://farsi-app.com/logo512.png",
          "articleSection": "Language Learning",
          "keywords": "persian language difficulty, farsi learning, persian grammar, persian alphabet"
        }}
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-purple-600 to-blue-700 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Is Persian Hard to Learn?
                <span className="block text-2xl md:text-3xl mt-2 text-purple-200">
                  Complete Difficulty Analysis & Learning Guide
                </span>
              </h1>
              <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
                Get the complete answer with detailed analysis of Persian language difficulty, 
                comparison with other languages, and proven learning strategies
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/alphabet-learning" 
                  className="px-8 py-4 bg-white text-purple-600 font-bold rounded-lg hover:bg-gray-100 transition duration-300 text-lg"
                >
                  Start Learning Persian
                </Link>
                <Link 
                  to="/learn-farsi-fast" 
                  className="px-8 py-4 border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-purple-600 transition duration-300 text-lg"
                >
                  Learn Persian Fast
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Answer Section */}
        <div className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                The Short Answer
              </h2>
              <div className="bg-green-100 border-l-4 border-green-500 p-6 max-w-4xl mx-auto">
                <p className="text-xl text-green-800 font-semibold">
                  Persian is <strong>moderately difficult</strong> to learn, but often easier than people expect. 
                  While the alphabet and some grammar concepts present challenges, 
                  Persian has many features that make it more approachable than other languages.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Difficulty Factors Analysis */}
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Persian Language Difficulty Breakdown
              </h2>
              <p className="text-xl text-gray-600">
                Detailed analysis of what makes Persian easy or challenging to learn
              </p>
            </div>
            
            <div className="space-y-8">
              {difficultyFactors.map((factor, index) => (
                <div key={index} className="bg-white rounded-lg p-8 shadow-lg">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">{factor.factor}</h3>
                    <span className={`px-4 py-2 rounded-full text-white font-semibold ${
                      factor.difficulty === 'Easy' ? 'bg-green-500' :
                      factor.difficulty === 'Easy to Medium' ? 'bg-blue-500' :
                      factor.difficulty === 'Medium' ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`}>
                      {factor.difficulty}
                    </span>
                  </div>
                  
                  <p className="text-lg text-gray-600 mb-6">{factor.description}</p>
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="text-lg font-semibold mb-3 text-red-600 flex items-center">
                        <FaTimes className="w-4 h-4 mr-2" />
                        Challenges
                      </h4>
                      <ul className="space-y-2">
                        {factor.challenges.map((challenge, challengeIndex) => (
                          <li key={challengeIndex} className="flex items-start text-gray-700">
                            <FaTimes className="w-4 h-4 text-red-500 mr-2 mt-1 flex-shrink-0" />
                            <span>{challenge}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold mb-3 text-green-600 flex items-center">
                        <FaCheck className="w-4 h-4 mr-2" />
                        Advantages
                      </h4>
                      <ul className="space-y-2">
                        {factor.advantages.map((advantage, advantageIndex) => (
                          <li key={advantageIndex} className="flex items-start text-gray-700">
                            <FaCheck className="w-4 h-4 text-green-500 mr-2 mt-1 flex-shrink-0" />
                            <span>{advantage}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Comparison with Other Languages */}
        <div className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Persian vs Other Languages
              </h2>
              <p className="text-xl text-gray-600">
                How Persian difficulty compares to other popular languages
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {comparisonWithOtherLanguages.map((comparison, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900">{comparison.language}</h3>
                    <span className={`px-3 py-1 rounded-full text-white text-sm font-semibold ${
                      comparison.difficulty === 'Much Easier' ? 'bg-green-500' :
                      comparison.difficulty === 'Easier' ? 'bg-blue-500' :
                      comparison.difficulty === 'Similar' ? 'bg-yellow-500' :
                      comparison.difficulty === 'Harder' ? 'bg-orange-500' :
                      'bg-red-500'
                    }`}>
                      {comparison.difficulty}
                    </span>
                  </div>
                  <ul className="space-y-2">
                    {comparison.reasons.map((reason, reasonIndex) => (
                      <li key={reasonIndex} className="flex items-start text-gray-700">
                        <FaStar className="w-4 h-4 text-blue-500 mr-2 mt-1 flex-shrink-0" />
                        <span>{reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Learning Tips */}
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                How to Make Persian Learning Easier
              </h2>
              <p className="text-xl text-gray-600">
                Proven strategies to overcome Persian language challenges
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {learningTips.map((tip, index) => (
                <div key={index} className="bg-white rounded-lg p-8 shadow-lg">
                  <div className="flex items-center mb-4">
                    {tip.icon}
                    <h3 className="text-xl font-semibold ml-3">{tip.title}</h3>
                  </div>
                  <p className="text-gray-600 mb-4">{tip.description}</p>
                  <ul className="space-y-2">
                    {tip.tips.map((tipItem, tipIndex) => (
                      <li key={tipIndex} className="flex items-start text-sm text-gray-700">
                        <FaLightbulb className="w-4 h-4 text-yellow-500 mr-2 mt-1 flex-shrink-0" />
                        {tipItem}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Success Stories */}
        <div className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Real Success Stories
              </h2>
              <p className="text-xl text-gray-600">
                Learners who discovered Persian wasn't as hard as they thought
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {successStories.map((story, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="bg-blue-100 rounded-full p-3 mr-4">
                      <FaHeart className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">{story.name}</h3>
                      <p className="text-blue-600 font-medium">{story.timeline}</p>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-4">{story.achievement}</p>
                  <p className="text-gray-700 italic">"{story.quote}"</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Time to Learn Section */}
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                How Long Does It Take to Learn Persian?
              </h2>
              <p className="text-xl text-gray-600">
                Realistic timelines based on different learning goals
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg p-8 shadow-lg text-center">
                <FaClock className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-4">Basic Conversation</h3>
                <p className="text-3xl font-bold text-blue-600 mb-2">3-6 months</p>
                <p className="text-gray-600 mb-4">
                  Daily practice, 30-60 minutes per day
                </p>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Greetings and introductions</li>
                  <li>• Basic vocabulary (500+ words)</li>
                  <li>• Simple present tense</li>
                  <li>• Reading basic texts</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg p-8 shadow-lg text-center">
                <FaGraduationCap className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-4">Intermediate Level</h3>
                <p className="text-3xl font-bold text-green-600 mb-2">1-2 years</p>
                <p className="text-gray-600 mb-4">
                  Regular practice, 1-2 hours per day
                </p>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Fluent conversations</li>
                  <li>• Complex grammar structures</li>
                  <li>• Reading Persian literature</li>
                  <li>• Writing essays</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg p-8 shadow-lg text-center">
                <FaStar className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-4">Advanced Proficiency</h3>
                <p className="text-3xl font-bold text-purple-600 mb-2">2-4 years</p>
                <p className="text-gray-600 mb-4">
                  Immersive learning, 2+ hours per day
                </p>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Native-like fluency</li>
                  <li>• Understanding poetry</li>
                  <li>• Academic Persian</li>
                  <li>• Teaching the language</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-16 bg-purple-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Start Your Persian Learning Journey?
            </h2>
            <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
              Don't let misconceptions about difficulty stop you. Persian is more approachable than you think!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/register" 
                className="px-8 py-4 bg-white text-purple-600 font-bold rounded-lg hover:bg-gray-100 transition duration-300 text-lg"
              >
                Start Learning Free
              </Link>
              <Link 
                to="/learn-farsi-fast" 
                className="px-8 py-4 border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-purple-600 transition duration-300 text-lg"
              >
                Learn Persian Fast
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default IsPersianHardToLearn;
